import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ResponseOfCertificate,
  ResponseOfCertificateTemplate,
  ResponseOfCertificateTheme,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import CertificatePreview from "./CertificatePreview";

const CertificateDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    certificateService,
    certificatesUrl,
    certificateEditUrl,
    certificateListUrl,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const certificateResult = useQuery<ResponseOfCertificate>(
    ["certificate", id],
    () => certificateService.getCertificate(id!),
    {
      enabled: !!id,
    },
  );

  const {
    data: certificateTemplateData,
    isLoading: isLoadingCertificateTemplate,
  } = useQuery<ResponseOfCertificateTemplate>(
    [
      "certificateTemplate",
      certificateResult.data?.data?.certificateTemplateId,
    ],
    () =>
      certificateService.getCertificateTemplate(
        certificateResult.data?.data?.certificateTemplateId!,
      ),
    {
      enabled: !!certificateResult.data?.data?.certificateTemplateId,
    },
  );

  const {
    data: certificateThemeData,
    isLoading: isLoadingCertificateTheme,
  } = useQuery<ResponseOfCertificateTheme>(
    ["certificateTheme", certificateResult.data?.data?.certificateThemeId],
    () =>
      certificateService.getCertificateTheme(
        certificateResult.data?.data?.certificateThemeId!,
      ),
    {
      enabled: !!certificateResult.data?.data?.certificateThemeId,
    },
  );

  const certificate = certificateResult.data?.data;
  useTitle(certificate?.name || "Certificate Details");

  if (!id) return <Navigate to={certificatesUrl()} replace />;
  if (certificateResult.isLoading) return <Loader />;
  if (certificateResult.isError) {
    console.log(certificateResult.error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      await registerPromise(certificateService.deleteCertificate(id));
      queryClient.invalidateQueries("certificates");
      deleteOpenActions.setFalse();
      navigate(certificatesUrl());
    } catch (error) {
      console.log(error);
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const rows = [
    {
      title: t({ id: "id" }),
      component: certificate?.id && <span>{certificate.id}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: certificate?.organizationName && (
        <span>{certificate.organizationName}</span>
      ),
    },
    {
      title: t({ id: "certificateFormNameLabel" }),
      component: certificate?.name && <span>{certificate.name}</span>,
    },
    {
      title: t({ id: "certificateFormValueLabel" }),
      component: certificate?.value && <span>{certificate.value}</span>,
    },
    {
      title: t({ id: "certificateFormDescriptionLabel" }),
      component: certificate?.description && (
        <span>{certificate.description}</span>
      ),
    },
    RowElement(
      t({ id: "certificateTemplateLabel" }),
      certificateTemplateData?.data?.name!,
      isLoadingCertificateTemplate,
    ),
    RowElement(
      t({ id: "certificateThemeLabel" }),
      certificateThemeData?.data?.name!,
      isLoadingCertificateTheme,
    ),
  ].filter((r) => !!r.component);

  const backButtonProps = location.state?.backButtonProps;

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateListUrl()}
        labelKey="backToListButtonLabel"
        {...backButtonProps}
      />
      <h2>{certificate?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={certificateEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!certificateResult.isLoading && (
        <CertificatePreview certificate={certificateResult?.data?.data!} />
      )}

      <DeleteEntityDialog
        dialogTitleKey="certificateDeleteTitle"
        contentKey="certificateDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
    </>
  );
};

export default CertificateDetails;
